import { useState } from 'react';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { IMAGE_NAME_PRESETS, isImageNamePreset } from './imageNamePresets';

type Props = {
  /** 対象画像のラベル (例: メイン画像 / 画像 3) */
  imageLabel: string;
  initialName: string;
  isSaving?: boolean;
  /** trim 済みの名前で呼ばれる。空文字 = 名前のクリア */
  onSave: (name: string) => void;
  onCancel: () => void;
};

/** 「その他（自由記述）」を表すセレクト値 (プリセットのラベルと衝突しない値) */
const OTHER_VALUE = '__other__';

/**
 * 商品画像の「名前」編集モーダル。
 * Amazon / マニュアル商品詳細ページで共用する。
 * プリセットからの選択を基本とし、「その他（自由記述）」でテキスト入力できる。
 * 親側で開くときだけマウントする (state を initialName で初期化するため)。
 */
export function ImageNameDialog({ imageLabel, initialName, isSaving, onSave, onCancel }: Props) {
  const [selection, setSelection] = useState<string>(() => {
    if (!initialName) return '';
    return isImageNamePreset(initialName) ? initialName : OTHER_VALUE;
  });
  const [customName, setCustomName] = useState<string>(() =>
    initialName && !isImageNamePreset(initialName) ? initialName : ''
  );

  const resolvedName = selection === OTHER_VALUE ? customName.trim() : selection;
  const handleSave = () => onSave(resolvedName);

  return (
    <ConfirmDialog
      isOpen
      title={`${imageLabel}の名前を編集`}
      variant="info"
      confirmLabel="保存"
      isLoading={isSaving}
      onConfirm={handleSave}
      onCancel={onCancel}
      message=""
    >
      <div className="image-name-dialog-body">
        <label className="image-name-dialog-label" htmlFor="image-name-dialog-select">
          名前
        </label>
        <select
          id="image-name-dialog-select"
          className="image-name-dialog-select"
          value={selection}
          onChange={(e) => setSelection(e.target.value)}
          autoFocus
        >
          <option value="">（名前なし）</option>
          {IMAGE_NAME_PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {preset}
            </option>
          ))}
          <option value={OTHER_VALUE}>その他（自由記述）</option>
        </select>
        {selection === OTHER_VALUE && (
          <input
            className="image-name-dialog-input"
            type="text"
            maxLength={100}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            onKeyDown={(e) => {
              // IME 変換確定の Enter では保存しない (keyCode 229 は Safari 対策)
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === 'Enter' && !isSaving) handleSave();
            }}
            placeholder="例: 成分表、受賞バッジ"
            autoFocus
          />
        )}
        <p className="image-name-dialog-note">
          ここで付けた名前は、動画ストーリーボードの生成時に参照画像の説明としてAIに渡され、シーンの演出に活かされます。
        </p>
      </div>
    </ConfirmDialog>
  );
}
